import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiChevronDown, FiInfo, FiShield, FiLock, FiTag } = FiIcons;

const policies = [
  {
    id: 'terms-and-conditions',
    title: 'Terms & Conditions',
    icon: FiInfo,
    content: (
      <div className="space-y-4 text-charcoal/70 font-light leading-relaxed">
        <p className="italic text-charcoal/90 mb-6">Welcome to Maison De Charm. By accessing our website or dining at our establishment, you agree to comply with the following terms and conditions.</p>
        <ul className="space-y-3 list-none">
          <li className="flex gap-3"><span className="text-gold">✦</span> Reservations are subject to availability.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Maison De Charm reserves the right to refuse service when necessary.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Menu items and prices may change without prior notice.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Guests are responsible for informing staff of any allergies or dietary restrictions.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Any misuse of the website, fraudulent bookings, or inappropriate conduct may result in restricted access.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> All content, logos, branding, and media on this website are the property of Maison De Charm and may not be copied or reused without permission.</li>
        </ul>
      </div>
    )
  },
  {
    id: 'privacy-policy',
    title: 'Privacy Policy',
    icon: FiShield,
    content: (
      <div className="space-y-4 text-charcoal/70 font-light leading-relaxed">
        <p className="italic text-charcoal/90 mb-6">Maison De Charm respects your privacy and is committed to protecting your personal information.</p>
        <ul className="space-y-3 list-none">
          <li className="flex gap-3"><span className="text-gold">✦</span> We may collect personal details such as name, email, phone number, and reservation details when you interact with our website.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Your information is used only for reservations, customer service, marketing updates, and improving user experience.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> We do not sell or share your personal information with third parties unless required by law.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Payment information is securely processed through trusted payment providers.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> By using our website, you consent to the collection and use of information according to this policy.</li>
        </ul>
      </div>
    )
  },
  {
    id: 'cookie-policy',
    title: 'Cookie Policy',
    icon: FiLock,
    content: (
      <div className="space-y-4 text-charcoal/70 font-light leading-relaxed">
        <p className="italic text-charcoal/90 mb-6">Maison De Charm uses cookies to improve website functionality and enhance your browsing experience.</p>
        <ul className="space-y-3 list-none">
          <li className="flex gap-3"><span className="text-gold">✦</span> Cookies help us understand website traffic and user behavior.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Some cookies are necessary for proper website functionality.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Analytics cookies may be used to improve website performance.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Users may disable cookies through browser settings, though some features may not function correctly.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Continued use of the website implies consent to our cookie usage.</li>
        </ul>
      </div>
    )
  },
  {
    id: 'promotional-terms',
    title: 'Promotional Terms & Conditions',
    icon: FiTag,
    content: (
      <div className="space-y-4 text-charcoal/70 font-light leading-relaxed">
        <p className="font-serif text-lg text-wine mb-4 underline decoration-gold/30 underline-offset-8">10% OFF Promotional Flyer Offer</p>
        <ul className="space-y-3 list-none">
          <li className="flex gap-3"><span className="text-gold">✦</span> 10% discount per flyer is only applicable for Ala Carte.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> No minimum spend is required.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Flyers can be used for a maximum of up to 4 pax per table.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> No splitting of bills is allowed.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Promos cannot coincide with other promotions, discounts, or special event menus.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Customers must present the original Maison De Charm promotional flyer at the restaurant.</li>
          <li className="flex gap-3"><span className="text-gold">✦</span> Maison De Charm reserves the right to amend or withdraw the promotion without prior notice.</li>
        </ul>
      </div>
    )
  }
];

export const PolicyAccordions = () => {
  const location = useLocation();
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const pathMap = {
      '/terms': 'terms-and-conditions',
      '/terms/Privacy': 'privacy-policy',
      '/terms/Cookie': 'cookie-policy',
      '/terms/Promo': 'promotional-terms'
    };

    // Exact match or fallback for root /terms
    const currentPath = location.pathname;
    const targetId = pathMap[currentPath] || 'terms-and-conditions';
    
    setOpenId(targetId);

    // Smooth scroll to the expanded section
    setTimeout(() => {
      const element = document.getElementById(targetId);
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 150);

  }, [location.pathname]);

  return (
    <section className="py-24 md:py-32 bg-ivory">
      <div className="container mx-auto px-6 md:px-14 max-w-4xl">
        
        <div className="space-y-4">
          {policies.map((policy) => (
            <div 
              key={policy.id} 
              id={policy.id}
              className="bg-white rounded-xl shadow-sm border border-gold/10 overflow-hidden transition-all duration-500 hover:shadow-md"
            >
              <button
                onClick={() => setOpenId(openId === policy.id ? null : policy.id)}
                className="w-full px-6 md:px-10 py-6 flex items-center justify-between group transition-colors text-left"
              >
                <div className="flex items-center gap-5">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-500 ${openId === policy.id ? 'bg-wine text-white' : 'bg-gold/10 text-gold group-hover:bg-gold/20'}`}>
                    <SafeIcon icon={policy.icon} className="text-lg" />
                  </div>
                  <span className={`font-serif text-xl md:text-2xl transition-colors duration-500 ${openId === policy.id ? 'text-wine' : 'text-charcoal/80 group-hover:text-charcoal'}`}>
                    {policy.title}
                  </span>
                </div>
                <motion.div
                  animate={{ rotate: openId === policy.id ? 180 : 0 }}
                  transition={{ duration: 0.4, ease: "circOut" }}
                  className={`${openId === policy.id ? 'text-wine' : 'text-gold/40'}`}
                >
                  <SafeIcon icon={FiChevronDown} className="text-2xl" />
                </motion.div>
              </button>

              <AnimatePresence initial={false}>
                {openId === policy.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.04, 0.62, 0.23, 0.98] }}
                  >
                    <div className="px-6 md:px-24 pb-10 pt-2 border-t border-gold/5">
                      {policy.content}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-20 text-center border-t border-gold/10 pt-10"
        >
          <p className="text-charcoal/40 text-[11px] tracking-[0.2em] uppercase italic">
            For additional inquiries, please contact Maison De Charm directly.
          </p>
        </motion.div>

      </div>
    </section>
  );
};